export type IEventHandler<TArgs> = (args: TArgs) => void;

class Subscription<TArgs> {
	isExecuted = false;

	constructor(public handler: IEventHandler<TArgs>, public isOnce: boolean) {}

	execute(args: TArgs) {
		if (!this.isOnce || !this.isExecuted) {
			this.isExecuted = true;
			this.handler(args);
		}
	}
}

export class EventDispatcher<TArgs = void> {
	private subscriptions: Subscription<TArgs>[] = [];

	dispatch(args: TArgs): void {
		for (const sub of [...this.subscriptions]) {
			sub.execute(args);
			this.cleanup(sub);
		}
	}

	forward(dispatcher: EventDispatcher<TArgs>): void {
		this.subscribe((args) => dispatcher.dispatch(args));
	}

	subscribe(fn: IEventHandler<TArgs>): EventDispatcher<TArgs> {
		if (fn) {
			this.subscriptions.push(new Subscription<TArgs>(fn, false));
		}
		return this;
	}

	one(fn: IEventHandler<TArgs>): EventDispatcher<TArgs> {
		if (fn) {
			this.subscriptions.push(new Subscription<TArgs>(fn, true));
		}
		return this;
	}

	has(fn: IEventHandler<TArgs>): boolean {
		if (!fn) {
			return false;
		}
		return this.subscriptions.some((sub) => sub.handler === fn);
	}

	unsubscribe(fn: IEventHandler<TArgs>): EventDispatcher<TArgs> {
		if (!fn) {
			return this;
		}

		for (let i = 0; i < this.subscriptions.length; i++) {
			if (this.subscriptions[i].handler === fn) {
				this.subscriptions.splice(i, 1);
				break;
			}
		}

		return this;
	}

	clear(): void {
		this.subscriptions.splice(0, this.subscriptions.length);
	}

	private cleanup(sub: Subscription<TArgs>) {
		if (sub.isOnce && sub.isExecuted) {
			const i = this.subscriptions.indexOf(sub);
			if (i > -1) {
				this.subscriptions.splice(i, 1);
			}
		}
	}
}
